'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { JSX } from 'react';
import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { AnimatedButton } from '@/components/ui/animated-button';
import { Icon } from '@/components/ui/icon';
import { PlusSignIcon } from '@/components/ui/icon/glyphs';
const noopSubscribe = (): (() => void) => (): void => {};
const getClientSnapshot = (): boolean => true;
const getServerSnapshot = (): boolean => false;
interface LaunchpadHeaderProps {
  /** Optional right-aligned toolbar actions (search, filter, help, watchlist). */
  actions?: JSX.Element;
  /** Opens the create-proposal flow (dialog is mounted by the parent layout). */
  onLaunchMarketClick: () => void;
}

/**
 * Proposals page header: title and "Start a debate" on the right (desktop),
 * with optional toolbar actions rendered before the Launch button. On mobile
 * the launch button becomes a floating FAB (bottom-right, above the bottom
 * nav) and the actions sit on the heading row. The create-proposal sheet is
 * mounted by the parent (`LaunchpadFeedLayout`) so it does not insert flex
 * layout between this title row and the feed.
 */
export function LaunchpadHeader(t0) {
  const $ = _c(27);
  const {
    actions,
    onLaunchMarketClick
  } = t0;
  const t = useTranslations("launchpad.feed");
  const mounted = useSyncExternalStore(noopSubscribe, getClientSnapshot, getServerSnapshot);
  let t1;
  if ($[0] !== t) {
    t1 = t("launchCta");
    $[0] = t;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== onLaunchMarketClick || $[3] !== t1) {
    t2 = <div className="fixed right-4 z-sticky bottom-[calc(5.5rem+env(safe-area-inset-bottom))] sm:hidden"><AnimatedButton prefix={PlusSignIcon} className="h-14 w-14 rounded-full shadow-elevation-sticky" onClick={onLaunchMarketClick} aria-label={t1} /></div>;
    $[2] = onLaunchMarketClick;
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const mobileFab = t2;
  let t3;
  if ($[5] !== t) {
    t3 = t("title");
    $[5] = t;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t3) {
    t4 = <h1 className="min-w-0 text-heading font-bold">{t3}</h1>;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const t5 = actions !== undefined && actions;
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <Icon icon={PlusSignIcon} className="mr-1 size-4" />;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t) {
    t7 = t("launchCta");
    $[10] = t;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  let t8;
  if ($[12] !== onLaunchMarketClick || $[13] !== t7) {
    t8 = <AnimatedButton onClick={onLaunchMarketClick} className="hidden sm:inline-flex">{t6}{t7}</AnimatedButton>;
    $[12] = onLaunchMarketClick;
    $[13] = t7;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  let t9;
  if ($[15] !== t5 || $[16] !== t8) {
    t9 = <div className="flex items-center gap-2">{t5}{t8}</div>;
    $[15] = t5;
    $[16] = t8;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t4 || $[19] !== t9) {
    t10 = <div className="flex items-center justify-between gap-2">{t4}{t9}</div>;
    $[18] = t4;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== mobileFab || $[22] !== mounted) {
    t11 = mounted && createPortal(mobileFab, document.body);
    $[21] = mobileFab;
    $[22] = mounted;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11) {
    t12 = <>{t10}{t11}</>;
    $[24] = t10;
    $[25] = t11;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  return t12;
}